import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { FlowRunDetail } from "./flow-runs/FlowRunDetail";
import toast from "react-hot-toast";
import { StepRun } from "@/models/flows";
import { BASE_URL } from "@/dashboardQueries";
import { Loading } from "@/components/portal/Loading";
import { ButtonSpinner } from "@/components/ButtonSpinner";
import { useDarkMode } from "@/hooks";
import { CheckCircle } from "lucide-react";

export const ApprovalMode = ({ flowId }: { flowId: number }) => {
  const [order, setOrder] = useState<"oldest" | "newest">("oldest");
  const queryClient = useQueryClient();
  const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);
  const isDarkMode = useDarkMode();

  const { data, isLoading, isError, error, refetch } = useQuery(
    ["approvalRequired", flowId, order],
    async () => {
      const res = await axios.get(
        `${BASE_URL}/flows/${flowId}/approval_required?order=${order}`
      );
      return res.data;
    },
    {
      retry: false,
    }
  );

  // Mutation for approval/decline
  const decisionMutation = useMutation(
    async (decision: "approved" | "declined") => {
      if (!data?.stepRuns?.length) throw new Error("No step run to approve");
      const stepRun = data.stepRuns.find(
        (stepRun: StepRun) =>
          stepRun.step.stepType === "approval" && stepRun.status === "waiting"
      );
      if (!stepRun) throw new Error("No approval step in waiting state");
      return await axios.post(
        `${BASE_URL}/step-runs/${stepRun.id}/decision`,
        { decision },
        { headers: { "Content-Type": "application/json" } }
      );
    },
    {
      onSuccess: async (_, decision) => {
        setTimeout(() => {
          toast.success(
            decision === "approved"
              ? "Approval submitted successfully"
              : "Decline submitted successfully"
          );
          queryClient.invalidateQueries(["approvalRequired", flowId, order]);
          setIsSubmittingDecision(false);
        }, 750);
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.detail ||
            err?.message ||
            "Failed to submit decision"
        );
        setIsSubmittingDecision(false);
      },
    }
  );

  // Handler functions for approve/decline
  const handleApprove = useCallback(() => {
    setIsSubmittingDecision(true);
    decisionMutation.mutate("approved");
  }, [decisionMutation]);

  const handleDecline = useCallback(() => {
    setIsSubmittingDecision(true);
    decisionMutation.mutate("declined");
  }, [decisionMutation]);

  // Keyboard shortcut handler
  useEffect(() => {
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    const handler = (e: KeyboardEvent) => {
      // CMD/CTRL + A for Approve
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "a") {
        e.preventDefault();
        handleApprove();
      }
      // CMD/CTRL + D for Decline
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "d") {
        e.preventDefault();
        handleDecline();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleApprove, handleDecline]);

  let content = null;
  if (isLoading) {
    content = <div>Loading...</div>;
  } else if (isError && (error as any)?.response?.status === 404) {
    content = (
      <div className="w-full mt-64">
        <div>
          <CheckCircle
            className="h-10 w-10 text-green-500 mx-auto mb-2"
            aria-hidden="true"
          />
          <h3 className="text-2xl dark:text-white font-medium text-gray-900 text-center">
            All caught up!
          </h3>
          <p className="mt-2 text-sm text-zinc-500 text-center max-w-xl mx-auto">
            You've processed all pending approvals. New approvs will appear here
            when they need your review.
          </p>
        </div>
      </div>
    );
  } else if (isError) {
    content = <div>Error loading approval run.</div>;
  }

  return (
    <div className="relative h-full">
      {data && !data.message && !isError && (
        <div className="relative">
          <div className="flex items-center justify-center">
            <div className="dark:bg-dark-secondary p-3 mt-4 border dark:border-white/10 bg-white rounded-lg shadow-sm w-full max-w-2xl mx-auto">
              <div className="flex">
                {isSubmittingDecision ? (
                  <div className="flex items-center w-full justify-center">
                    <ButtonSpinner colour={isDarkMode ? "white" : "black"} />
                    <span className="dark:text-zinc-300 text-zinc-500">
                      Submitting decision...
                    </span>
                  </div>
                ) : (
                  <div className="flex justify-between w-full">
                    <button
                      className="text-sm dark:border-white/5 dark:bg-zinc-800 text-zinc-300 mr-2 flex items-center border rounded-md px-3 py-1 dark:hover:bg-zinc-800 flex cursor-pointer group transition duration-200 ease-in-out hover:border-white/10 hover:bg-zinc-100/70 dark:hover:border-zinc-400/30 dark:hover:text-zinc-400 text-zinc-500 hover:text-zinc-600 hover:border-zinc-300 hover:text-zinc-300"
                      onClick={handleApprove}
                      disabled={decisionMutation.isLoading}
                    >
                      Approve
                      <span className="ml-2 border dark:border-white/10 dark:bg-zinc-700 px-1 py-0 rounded-lg -mr-1 dark:text-white">
                        {navigator.platform.toUpperCase().indexOf("MAC") >=
                        0 ? (
                          <>⌘A</>
                        ) : (
                          <>CTRL + A</>
                        )}
                      </span>
                    </button>
                    <div className="text-center flex items-center">
                      <h5 className="text-zinc-500 text-xs uppercase">
                        Quick actions
                      </h5>
                    </div>
                    <button
                      className="text-sm dark:border-white/5 dark:bg-zinc-800 text-zinc-300 flex items-center border rounded-md px-3 py-1 dark:hover:bg-zinc-800 flex cursor-pointer group transition duration-200 ease-in-out hover:border-white/10 hover:bg-zinc-100/70 dark:hover:border-zinc-400/30 dark:hover:text-zinc-400 text-zinc-500 hover:text-zinc-600 hover:border-zinc-300 hover:text-zinc-300"
                      onClick={handleDecline}
                      disabled={decisionMutation.isLoading}
                    >
                      Reject
                      <span className="ml-2 border dark:border-white/10 dark:bg-zinc-700 px-1 py-0 rounded-lg -mr-1 dark:text-white">
                        {navigator.platform.toUpperCase().indexOf("MAC") >=
                        0 ? (
                          <>⌘D</>
                        ) : (
                          <>CTRL + D</>
                        )}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="max-w-2xl w-full mx-auto">
            <div className="">
              <div className="flex items-center justify-between py-4">
                <div>
                  <h5 className="text-zinc-500 text-sm">
                    {data.count} approval{data.count > 1 ? "s" : ""} pending
                  </h5>
                </div>
                <div>
                  <select
                    value={order}
                    onChange={(e) =>
                      setOrder(e.target.value as "oldest" | "newest")
                    }
                    className="block w-full rounded-md border-gray-300 dark:border-white/10 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-zinc-900 dark:text-gray-300 text-gray-900"
                  >
                    <option value="oldest">Oldest first</option>
                    <option value="newest">Newest first</option>
                  </select>
                </div>
              </div>

              <FlowRunDetail flowRun={data} expandTriggerInput />
            </div>
          </div>

          {/* Use absolute positioning and parent relative to match parent width */}
          {/* <div className="absolute left-0 right-0 bottom-0 mb-20 flex items-center justify-center">
            <div className="dark:bg-dark-secondary px-10 py-4 border dark:border-white/5 bg-white z-50 rounded-lg shadow-lg w-full max-w-2xl mx-auto">
              <div className="text-center mb-2">
                <h5 className="text-zinc-600 text-xs uppercase">
                  Quick actions
                </h5>
              </div>
              <div className="flex">
                {isSubmittingDecision ? (
                  <div className="flex items-center">
                    <ButtonSpinner colour={isDarkMode ? "white" : "black"} />
                    <span className="text-zinc-300">
                      Submitting decision...
                    </span>
                  </div>
                ) : (
                  <>
                    <button
                      className="dark:border-white/5 dark:bg-zinc-800 text-zinc-300 mr-2 flex items-center border rounded-md px-4 py-2 dark:hover:bg-zinc-800 flex cursor-pointer group transition duration-200 ease-in-out border-transparent hover:border-white/10 hover:bg-dark-primary text-zinc-500 hover:text-zinc-300"
                      onClick={handleApprove}
                      disabled={decisionMutation.isLoading}
                    >
                      Approve
                      <span className="ml-2 border dark:border-white/5 dark:bg-zinc-700 px-1 py-0 rounded-lg -mr-1">
                        {navigator.platform.toUpperCase().indexOf("MAC") >=
                        0 ? (
                          <>⌘A</>
                        ) : (
                          <>CTRL + A</>
                        )}
                      </span>
                    </button>
                    <button
                      className="dark:border-white/5 dark:bg-zinc-800 text-zinc-300 mr-2 flex items-center border rounded-md px-4 py-2 dark:hover:bg-zinc-800 flex cursor-pointer group transition duration-200 ease-in-out border-transparent hover:border-white/10 hover:bg-dark-primary text-zinc-500 hover:text-zinc-300"
                      onClick={handleDecline}
                      disabled={decisionMutation.isLoading}
                    >
                      Reject
                      <span className="ml-2 border dark:border-white/5 dark:bg-zinc-700 px-1 py-0 rounded-lg -mr-1">
                        {navigator.platform.toUpperCase().indexOf("MAC") >=
                        0 ? (
                          <>⌘D</>
                        ) : (
                          <>CTRL + D</>
                        )}
                      </span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div> */}
        </div>
      )}

      {(!data || isError) && content}
    </div>
  );
};
